/**
 * `/marketplace` sur mobile : en descendant dans la liste, l'en-tête du site sort de l'écran (la barre de
 * filtres remonte à sa place) et « Sauvegarder la recherche » se réduit à sa cloche. Un geste vers le haut
 * fait tout revenir.
 *
 * Seuils (Nielsen Norman Group) : il faut descendre de quelques pixels pour masquer et remonter un peu
 * plus pour réafficher, sinon l'en-tête clignote quand on relit une annonce.
 */

const HIDDEN_CLASS = "mp-scroll-down";
const HEADER_HEIGHT_VAR = "--mp-header-height";
const MOBILE_QUERY = "(max-width: 959.98px)";
const HIDE_AFTER_PX = 8;
const SHOW_AFTER_PX = 24;

export interface ScrollChromeState {
    hidden: boolean;
    /** Distance parcourue dans le sens courant depuis le dernier changement de sens. */
    travel: number;
}

/**
 * Décide si l'en-tête doit être masqué. `hideFrom` = position à partir de laquelle la barre de filtres est
 * collée en haut (avant, on voit encore le titre : rien à masquer).
 */
export function nextScrollChromeState(
    state: ScrollChromeState,
    previousY: number,
    currentY: number,
    hideFrom: number,
): ScrollChromeState {
    if (currentY <= hideFrom) {
        return { hidden: false, travel: 0 };
    }

    const delta = currentY - previousY;
    if (delta === 0) return state;

    const sameDirection = (delta > 0) === (state.travel >= 0);
    const travel = sameDirection ? state.travel + delta : delta;

    if (travel >= HIDE_AFTER_PX) return { hidden: true, travel };
    if (travel <= -SHOW_AFTER_PX) return { hidden: false, travel };

    return { hidden: state.hidden, travel };
}

/** Un panneau ou un menu est ouvert, ou la recherche du site a le focus : on ne masque rien. */
function isChromeLocked(header: HTMLElement): boolean {
    return (
        document.body.classList.contains("app-filter-open") ||
        document.body.style.overflow === "hidden" ||
        header.contains(document.activeElement) ||
        document.querySelector('[data-facet-dropdown-trigger][aria-expanded="true"]') !== null
    );
}

let initialized = false;

export function initMobileScrollChrome(): void {
    if (initialized || typeof window === "undefined") return;

    const header = document.getElementById("app-header");
    const chrome = document.querySelector<HTMLElement>(".marketplace-global-chrome");
    const bar = document.querySelector<HTMLElement>(".marketplace-global-chrome__action-bar");
    if (!header || !chrome || !bar) return;
    initialized = true;

    const root = document.documentElement;
    const mobile = window.matchMedia(MOBILE_QUERY);
    let state: ScrollChromeState = { hidden: false, travel: 0 };
    let lastY = window.scrollY;
    let ticking = false;

    const apply = (hidden: boolean): void => {
        if (hidden) {
            root.style.setProperty(HEADER_HEIGHT_VAR, `${header.offsetHeight}px`);
        }
        root.classList.toggle(HIDDEN_CLASS, hidden);
    };

    const show = (): void => {
        state = { hidden: false, travel: 0 };
        apply(false);
    };

    const update = (): void => {
        ticking = false;
        const currentY = window.scrollY;
        // Bas du titre dans la page = point où la barre de filtres vient se coller sous l'en-tête
        const hideFrom = chrome.getBoundingClientRect().bottom + currentY - header.offsetHeight;

        if (!mobile.matches || isChromeLocked(header)) {
            lastY = currentY;
            // Ex. le panneau Filtrer ramène la page en haut : l'en-tête doit y être en revenant
            if (state.hidden && (!mobile.matches || currentY <= hideFrom)) show();
            return;
        }

        const next = nextScrollChromeState(state, lastY, currentY, hideFrom);
        lastY = currentY;

        if (next.hidden !== state.hidden) apply(next.hidden);
        state = next;
    };

    window.addEventListener(
        "scroll",
        () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
        },
        { passive: true },
    );

    mobile.addEventListener("change", show);

    // Navigation au clavier : un lien ou la recherche de l'en-tête prend le focus, l'en-tête revient
    header.addEventListener("focusin", () => {
        if (state.hidden) show();
    });
}
